import { useQuery, useQueryClient } from '@tanstack/react-query';
import { callBizpart } from '../../../../Js/React/Services/common';
import { HighscoreFilterType, HighscoreItemList } from '../quiztypes';
import { useEffect } from 'react';
import { transformDateStringPropsToDate } from '../../../../FrontEndReact/src/Core/Utils/dateUtils';

const apiPath = 'api/speedquiz/';

function highscoreItemListMapper(dto: HighscoreItemList | undefined): HighscoreItemList | undefined {
	if (!dto) return dto;
	return transformDateStringPropsToDate<HighscoreItemList, keyof HighscoreItemList>(dto, ['StartTime']);
}

function highscoreItemListArrayMapper(dto: HighscoreItemList[] | undefined): HighscoreItemList[] | undefined {
	if (!dto) return dto;
	return dto.map((o) => highscoreItemListMapper(o)) as HighscoreItemList[];
}

const getHighscoresForUser = async (filter: HighscoreFilterType, max: number, totalOnly?: boolean) => {
	const path = `${apiPath}GetHighscoresForUser?unitTypeId=${filter.UnitTypeId}&max=${max}&totalOnly=${totalOnly ?? false}`;
	const result = await callBizpart<HighscoreItemList[]>(path);
	return highscoreItemListArrayMapper(result);
};

const getHighscore = async (pluginInstanceId: number, max: number) => {
	const path = `${apiPath}GetHighscore?id=${pluginInstanceId}&max=${max}`;
	const result = await callBizpart<HighscoreItemList>(path);
	return highscoreItemListMapper(result);
};

const getHighscoreFilterItems = async () => {
	const path = `${apiPath}GetHighscoreFilterItems`;
	const result = await callBizpart<HighscoreFilterType[]>(path);
	return result;
};

const QUERYKEY_HIGHSCORE_FOR_USER = 'QUIZ_HIGHSCORES_FOR_USER';
const QUERYKEY_HIGHSCORE = 'QUIZ_HIGHSCORE';
const QUERYKEY_HIGHSCORE_FILTERITEMS = 'QUIZ_HIGHSCORE_FILTERITEMS';

export const useGetHighscoreFilterItems = () => {
	const { data: filterItems } = useQuery({
		queryKey: [QUERYKEY_HIGHSCORE_FILTERITEMS],
		queryFn: async () => {
			const data = getHighscoreFilterItems();
			return data;
		},
	});
	return filterItems;
};

export const useGetHighscore = (pluginInstanceId?: number, max?: number) => {
	const { data: highscores } = useQuery({
		queryKey: [QUERYKEY_HIGHSCORE, pluginInstanceId],
		queryFn: async () => {
			if (pluginInstanceId && pluginInstanceId > 0) {
				const data = await getHighscore(pluginInstanceId, max ?? 10);
				return data;
			}
		},
		enabled: pluginInstanceId !== undefined && pluginInstanceId > 0,
	});
	return highscores;
};

export const useGetHighscoresForUser = (
	filter: HighscoreFilterType | undefined,
	max: number,
	totalOnly?: boolean,
	prefetchFilters?: HighscoreFilterType[]
) => {
	const queryClient = useQueryClient();
	const { data: highscores, isLoading } = useQuery({
		queryKey: [QUERYKEY_HIGHSCORE_FOR_USER, filter, max],
		queryFn: async () => {
			if (filter) {
				const data = await getHighscoresForUser(filter, max, totalOnly);
				return data;
			}
		},
		enabled: filter !== undefined,
	});

	useEffect(() => {
		if (prefetchFilters && prefetchFilters.length > 0) {
			prefetchFilters.forEach((f) => {
				if (f !== filter) {
					queryClient.prefetchQuery({
						queryKey: [QUERYKEY_HIGHSCORE_FOR_USER, f, max],
						queryFn: async () => {
							const data = await getHighscoresForUser(f, max, totalOnly);
							return data;
						},
					});
				}
			});
		}
	}, [prefetchFilters]);

	return {
		highscores,
		isLoading,
	};
};
